import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import React, { FunctionComponentElement } from 'react';
import styled, { css } from 'styled-components';

interface ITabsProps {
	className?: string;
	children?: React.ReactNode | React.ReactNode[];
	size?: 'medium' | 'large';
}

interface CloneExpandedProps {
	size: ITabsProps['size'];
}

export const Tabs = styled(({ className, children, size = 'medium' }: ITabsProps) => {
	const betterChildren = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			return React.cloneElement<CloneExpandedProps>(
				child as FunctionComponentElement<CloneExpandedProps>,
				{
					size,
				}
			);
		}
		return child;
	});

	return <div className={className}>{betterChildren}</div>;
})`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-end;
`;
interface ITabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	className?: string;
	children?: string | React.ReactNode;
	active?: boolean;
	disabled?: boolean;
	size?: ITabsProps['size'];
	onClick?: () => void;
}
export const TabV2 = styled(({ className, children, size = 'medium', onClick }: ITabProps) => {
	const isChildrenAString = typeof children === 'string';

	return (
		<button className={className} onClick={onClick}>
			{isChildrenAString ? (
				<Text variant={size === 'large' ? 'button' : 'button-small'} bold>
					{children}
				</Text>
			) : (
				children
			)}
		</button>
	);
})`
	position: relative;
	cursor: pointer;
	color: ${({ theme }) => theme.v2.color.neutral[1100]};
	outline: none;
	background-color: transparent;
	border: none;

	&:after {
		position: absolute;
		display: none;
		content: '';
		bottom: 0;
		left: 0;
		width: 100%;
		background-color: ${({ theme }) => theme.v2.color.neutral[1100]};
		height: 0.125rem;
	}

	&:hover {
		background-color: ${({ theme }) => theme.v2.color.neutral[200]};
		color: ${({ theme }) => theme.v2.color.neutral[1100]};
		&:after {
			display: block;
		}
	}

	${({ active, theme }) => {
		return active
			? css`
					background-color: ${({ theme }) => theme.v2.color.secondary[400]};
					color: ${theme.v2.color.neutral[1100]};
					&:after {
						display: block;
						height: 0.125rem;
					}
				`
			: ``;
	}}

	${({ theme, disabled }) =>
		disabled
			? css`
					cursor: not-allowed;
					color: ${theme.v2.color.neutral[400]};
				`
			: ''}

  ${({ size = 'medium' }) =>
		size === 'large'
			? `
            padding: 1rem 3.5rem;
        `
			: `
               padding: 0.375rem 0.5rem;

            `}
`;

export const Tab = styled(({ className, children, size = 'medium', onClick }: ITabProps) => {
	const isChildrenAString = typeof children === 'string';

	return (
		<button className={className} onClick={onClick}>
			{isChildrenAString ? (
				<Text variant={size === 'large' ? 'button' : 'button-small'}>{children}</Text>
			) : (
				children
			)}
		</button>
	);
})`
	position: relative;
	cursor: pointer;
	border-radius: 8px 8px 0 0;
	color: ${({ theme }) => theme.v2.color.neutral[700]};
	outline: none !important;
	background-color: transparent;
	border: none;

	&:after {
		position: absolute;
		display: none;
		content: '';
		bottom: 0;
		left: 0;
		width: 100%;
		background-color: ${({ theme }) => theme.v2.color.secondary[800]};
		height: 2px;
		border-radius: 4px 4px 0 0;
	}

	&:hover {
		background-color: ${({ theme }) => theme.v2.color.secondary[200]};
		color: ${({ theme }) => theme.v2.color.neutral[600]};
		&:after {
			display: block;
		}
	}

	${({ active, theme }) => {
		return active
			? css`
					color: ${theme.v2.color.secondary[800]};
					-webkit-text-stroke: 0.3px ${theme.v2.color.secondary[800]};
					&:after {
						display: block;
						height: 4px;
					}
				`
			: ``;
	}}

	${({ theme, disabled }) =>
		disabled
			? css`
					cursor: not-allowed;
					color: ${theme.v2.color.neutral[400]};
				`
			: ''}

  ${({ size = 'medium' }) =>
		size === 'large'
			? `
            padding: 1rem 3.5rem;
        `
			: `
               padding: 0.5rem 1.5rem;

            `}
`;
